import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { useLogo } from '../../context/LogoContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { LayoutDashboard, Users, Send, FileCheck, AlertTriangle, FileText, LogOut, Menu, X, Wallet, Settings, Building, ScrollText, Shield, ArrowRightLeft, BadgeJapaneseYen, HelpCircle, PanelBottom, CreditCard, Mail, Gift, FileBadge } from 'lucide-react';
import { useState } from 'react';
import LanguageSelector from '../LanguageSelector';

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const { logoUrl } = useLogo();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { icon: LayoutDashboard, label: t('admin_nav_dashboard'), path: '/admin/dashboard' },
    { icon: Users, label: t('admin_nav_users'), path: '/admin/users' },
    { icon: Send, label: t('admin_nav_transfers'), path: '/admin/transfers' },
    { icon: Wallet, label: t('admin_nav_deposits'), path: '/admin/deposits' },
    { icon: FileCheck, label: t('admin_nav_kyc'), path: '/admin/kyc' },
    { icon: FileText, label: t('admin_nav_income_cert'), path: '/admin/income-certificates' },
    { icon: CreditCard, label: t('admin_nav_residence_card'), path: '/admin/residence-cards' },
    { icon: Mail, label: t('admin_nav_email'), path: '/admin/email' },
    { icon: Gift, label: t('admin_nav_referrals'), path: '/admin/referrals' },
    { icon: AlertTriangle, label: t('admin_nav_aml'), path: '/admin/aml' },
    { icon: FileBadge, label: t('admin_nav_balance_cert'), path: '/admin/balance-certificates' },
    { icon: FileText, label: t('admin_nav_reports'), path: '/admin/reports' },
    { icon: Settings, label: t('admin_nav_settings'), path: '/admin/settings' },
    { icon: Building, label: t('admin_nav_company'), path: '/admin/company' },
    { icon: ScrollText, label: t('admin_nav_terms'), path: '/admin/terms' },
    { icon: Shield, label: t('admin_nav_privacy'), path: '/admin/privacy' },
    { icon: ArrowRightLeft, label: t('admin_nav_transfer_methods'), path: '/admin/transfer-methods' },
    { icon: BadgeJapaneseYen, label: t('admin_nav_pricing'), path: '/admin/pricing' },
    { icon: HelpCircle, label: t('admin_nav_faq'), path: '/admin/faq' },
    { icon: PanelBottom, label: t('admin_nav_footer'), path: '/admin/footer' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-white/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin/dashboard" className="flex items-center gap-2" data-testid="admin-logo">
              <img src={logoUrl} alt="PRC Remit" className="h-9 w-9 rounded-full object-cover" />
              <span className="font-logo text-xl font-bold text-primary">PRC Remit</span>
              <span className="text-sm text-accent">{t('admin_title')}</span>
            </Link>
            <div className="flex items-center gap-3">
              <LanguageSelector />
              <div className="hidden md:block text-sm">
                <div className="text-primary font-medium">{user?.full_name}</div>
                <div className="text-muted-foreground text-xs">{t('admin_title')}</div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="admin-mobile-menu-toggle"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="hidden md:flex gap-2 rounded-full"
                data-testid="admin-logout-button"
              >
                <LogOut className="h-4 w-4" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-border bg-card" data-testid="admin-mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg smooth-transition ${
                  location.pathname === item.path
                    ? 'bg-accent/10 text-accent font-medium'
                    : 'text-muted-foreground hover:bg-secondary'
                }`}
                data-testid={`admin-mobile-nav-${item.path}`}
              >
                <item.icon className="h-5 w-5" />
                {item.label}
              </Link>
            ))}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-muted-foreground hover:bg-secondary smooth-transition"
              data-testid="admin-mobile-logout-button"
            >
              <LogOut className="h-5 w-5" />
              {t('logout')}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="hidden md:block">
            <nav className="space-y-2 sticky top-24">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg smooth-transition ${
                    location.pathname === item.path
                      ? 'bg-accent/10 text-accent font-medium'
                      : 'text-muted-foreground hover:bg-secondary'
                  }`}
                  data-testid={`admin-nav-${item.path}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <main className="md:col-span-3">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}
